import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CapsuleNavbar } from './components/ui/capsule-navbar';
import { Hero } from './components/ui/hero';
import { Features4 } from './components/ui/features-4';
import Component from './components/ui/image-auto-slider';
import Testimonials from './components/ui/testimonials-columns-1-demo';
import { CTASection } from './components/ui/cta-with-rectangle';
import DownloadPage from './components/ui/download-page';
import { Footer } from './components/ui/footer-section';
import { SignIn } from './components/ui/login-2';
import { Signup } from './components/ui/signup';
import AnimatedTextCycle from './components/ui/animated-text-cycle';
import OAuthPage from './components/ui/oauth-page';
import OAuthCallback from './components/ui/oauth-callback';
import ProfilePage from './components/ui/profile-page';
import CompleteProfilePage from './components/ui/complete-profile-page';
import AccessCodesPage from './components/ui/access-codes-page';
import Loader from './components/ui/loader';
import AuthSuccess from './components/ui/auth-success';
import AuthError from './components/ui/auth-error';
import TestActivationCodes from './components/ui/test-activation-codes';
import TestAuth from './components/ui/test-auth';
import './App.css';

function App() {
  const [showSignIn, setShowSignIn] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showLoader, setShowLoader] = useState(true);

  // Simulate app loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  // Function to switch from signup to sign in
  const switchToSignIn = () => {
    setShowSignup(false);
    setShowSignIn(true);
  };

  // Function to switch from sign in to signup
  const switchToSignup = () => {
    setShowSignIn(false);
    setShowSignup(true);
  };

  // Function to close modals
  const closeModals = () => {
    setShowSignIn(false);
    setShowSignup(false);
  };

  // Handle loader exit
  const handleLoaderExit = () => {
    setShowLoader(false);
  };

  // Show loader while app is initializing
  if (showLoader && isLoading) {
    return <Loader onExit={handleLoaderExit} />;
  }

  return (
    <Router>
      <AuthProvider>
        <div className="App bg-background text-foreground h-screen overflow-hidden">
          {showLoader && <Loader onExit={handleLoaderExit} />}
          <Routes>
            <Route path="/download" element={<DownloadPage />} />
            <Route path="/oauth" element={<OAuthPage />} />
            <Route path="/oauth/google" element={<OAuthPage />} />
            <Route path="/oauth/github" element={<OAuthPage />} />
            <Route path="/oauth/callback" element={<OAuthCallback />} />
            {/* Add the specific OAuth callback routes */}
            <Route path="/api/auth/callback/google" element={<OAuthCallback />} />
            <Route path="/api/auth/callback/github" element={<OAuthCallback />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/access-codes" element={<AccessCodesPage />} />
            <Route path="/complete-profile" element={<CompleteProfilePage />} />
            {/* New OAuth redirect routes */}
            <Route path="/auth/success" element={<AuthSuccess />} />
            <Route path="/auth/error" element={<AuthError />} />
            {/* Test routes */}
            <Route path="/test-activation-codes" element={<TestActivationCodes />} />
            <Route path="/test-auth" element={<TestAuth />} />
            <Route path="/" element={
              <>
                {showSignIn ? (
                  <SignIn onClose={closeModals} onSwitchToSignup={switchToSignup} />
                ) : showSignup ? (
                  <Signup onClose={closeModals} onSwitchToLogin={switchToSignIn} />
                ) : (
                  <>
                    <CapsuleNavbar 
                      onLoginClick={() => setShowSignIn(true)} 
                    />
                    <div className="overflow-y-auto h-full">
                      <Hero
                        eyebrow="INTRODUCING CAUDE"
                        title={
                          <>
                            <div className="whitespace-nowrap">
                              <span className="font-instrument-serif font-normal">Your intelligent </span>
                              <AnimatedTextCycle 
                                words={[
                                  "AI assistant",
                                  "Image creator",
                                  "Video generator",
                                  "OSINT analyzer",
                                  "Search engine",
                                  "Chat companion"
                                ]}
                                interval={3000}
                                className="font-instrument-serif font-normal italic text-primary"
                              />
                            </div>
                            <div className="font-instrument-serif font-normal">
                              for every task
                            </div>
                          </>
                        }
                        subtitle="Caude is an intelligent AI assistant featuring chat, image generation, video creation, OSINT analysis, and web search capabilities all in one powerful interface."
                        ctaText="Download now"
                        ctaLink="/download"
                        mockupImage={{
                          src: "/hero.png",
                          alt: "Caude AI Interface",
                          width: 1274,
                          height: 1043
                        }}
                      />
                      <div className="py-8" id="features">
                        <Features4 />
                      </div>
                      <div className="py-2" id="gallery">
                        <Component />
                      </div>
                      <div className="py-2" id="testimonials">
                        <Testimonials />
                      </div>
                      <div className="py-8" id="download">
                        <CTASection
                          title="Ready to experience Caude?"
                          description="Join thousands of users who have already experienced the power of advanced AI assistance."
                          action={{
                            text: "Download Caude Now",
                            href: "/download",
                            variant: "default"
                          }}
                          withGlow={true}
                        />
                      </div>
                      <Footer />
                    </div>
                  </>
                )}
              </>
            } />
          </Routes>
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;